import React, { useState, useMemo, useLayoutEffect } from "react";
import {
	Box,
	Paper,
	TextField,
	ThemeProvider,
	Typography,
	Divider,
	IconButton,
	Tooltip,
} from "@material-ui/core";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import { SelectTheme } from "../../utils/utils";
import { getTranslationObject, isEmptyTranslation } from "./utils/helpers";
import { InfoPanel } from "../Common/Panels/InfoPanel";
import DeleteConfirmationModal from "../Common/Modals/DeleteConfirmationModal";

export const TranslationRow = React.memo(
	function TranslationRow({
		rowData,
		selectedLang,
		onSync,
		onDelete,
		resources,
	}) {
		// Info panel state.
		const [info, setInfo] = useState({});
		// State to control the delete confirmation modal.
		const [deleteModalOpen, setDeleteModalOpen] = useState(false);
		// Track if the translation has been saved (synced).
		const [isSynced, setIsSynced] = useState(
			() => !isEmptyTranslation(getTranslationObject(rowData, selectedLang))
		);
		// Fields refused by the sync because their value duplicates another option's.
		const [duplicateKeys, setDuplicateKeys] = useState({});

		// Derive the initial translation object for the selected language.
		const initialLangData = useMemo(
			() => getTranslationObject(rowData, selectedLang),
			[rowData, selectedLang]
		);

		// When initialLangData changes, update the synced state.
		useLayoutEffect(() => {
			setIsSynced(!isEmptyTranslation(initialLangData));
			setDraft(initialLangData);
		}, [initialLangData]);

		const [draft, setDraft] = useState(initialLangData);

		const clearDuplicateState = () => {
			if (Object.keys(duplicateKeys).length > 0) {
				setInfo((prev) => ({ ...prev, showTransition: false }));
				setDuplicateKeys({});
			}
		};

		const handleSingleChange = (e) => {
			const newVal = e.target.value;
			setDraft((prev) => ({
				...prev,
				item: { ...prev.item, value: newVal },
			}));
			// Mark as unsynced as the user is editing.
			setIsSynced(false);
			clearDuplicateState();
		};

		const handleGroupChange = (key, e) => {
			const newVal = e.target.value;
			setDraft((prev) => ({
				...prev,
				group: prev.group.map((item) =>
					item.key === key ? { ...item, value: newVal } : item
				),
			}));
			setIsSynced(false);
			clearDuplicateState();
		};

		const showInfo = (message, severity) => {
			setInfo({ showTransition: true, severity, message });
			setTimeout(() => {
				setInfo((prev) => ({ ...prev, showTransition: false }));
			}, 3000);
		};

		const handleBlur = async (fieldKey, newValue) => {
			if (!newValue) return;

			// If previous value is the same as the new value, do nothing.
			const previousValue = rowData.translations[selectedLang];
			if (
				previousValue.group &&
				previousValue.group.find((item) => item.key === fieldKey)?.value ===
					newValue
			) {
				return;
			}
			if (previousValue.item?.value === newValue) {
				return;
			}

			const langData = draft;
			const syncEvent = {
				rowId: rowData.id,
				rowType: rowData.type,
				language: selectedLang,
				translations: {},
			};

			// For a single translation:
			if (langData.item) {
				if (fieldKey !== langData.item.key) return;
				syncEvent.translations[fieldKey] = newValue;
			} else if (langData.group) {
				// Check if all group fields are filled
				const allFilled = langData.group.every(
					(item) => item.value.trim().length > 0
				);

				if (!allFilled) {
					setInfo((prev) => ({
						...prev,
						showTransition: true,
						severity: "warning",
						message: resources["FillAllFields"],
					}));
					return;
				}

				langData.group.forEach((item) => {
					syncEvent.translations[item.key] = item.value;
				});
			}

			const updatedRowData = {
				...rowData,
				translations: { ...rowData.translations, [selectedLang]: draft },
			};

			const syncResponse = await onSync(syncEvent, updatedRowData);

			if (syncResponse.success) {
				showInfo(resources["TranslationSaved"], "success");

				// Mark as synced after successful sync.
				setIsSynced(true);
				setDuplicateKeys({});
			} else if (syncResponse.duplicate) {
				setDuplicateKeys((prev) => ({ ...prev, [fieldKey]: true }));
				// stays visible until the value is edited, unlike the auto-hiding chips
				setInfo({
					showTransition: true,
					severity: "danger",
					message:
						resources["DuplicateOptionTranslation"] ||
						"Options of the same question must have different translations",
				});
			} else {
				showInfo(resources["TranslationSaveFailed"], "warning");
			}
		};

		const translationCompleted = !isEmptyTranslation(draft);

		const handleSubmitDelete = async () => {
			const syncEvent = {
				rowId: rowData.id,
				rowType: rowData.type,
				language: selectedLang,
				translations: {},
			};

			// Prepare the translations to be emptied
			if (draft.item) {
				syncEvent.translations[draft.item.key] = "";
			} else if (draft.group) {
				draft.group.forEach((item) => {
					syncEvent.translations[item.key] = "";
				});
			}

			// Create the updated row data but don't apply it yet
			let newDraft;
			if (draft.item) {
				newDraft = {
					...draft,
					item: { ...draft.item, value: "" },
				};
			} else if (draft.group) {
				newDraft = {
					...draft,
					group: draft.group.map((item) => ({ ...item, value: "" })),
				};
			}

			const updatedRowData = {
				...rowData,
				translations: { ...rowData.translations, [selectedLang]: newDraft },
			};

			// First call the API
			const deleteResponse = await onDelete(syncEvent, updatedRowData);

			// Only update local state if API call succeeded
			if (deleteResponse && deleteResponse.success) {
				// Update the local draft state to empty values
				setDraft(newDraft);
				setIsSynced(false);
				showInfo(resources["TranslationDeleted"], "success");
			} else {
				showInfo(resources["TranslationDeleteFailed"], "danger");
			}

			setDeleteModalOpen(false);
		};

		return (
			<Paper
				style={{
					border: "1px solid #e0e0e0",
					boxShadow:
						"0 1px 2px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1)",
					overflow: "hidden",
				}}
			>
				<Box display="flex" alignItems="stretch">
					<Box
						style={{
							padding: "8px 16px",
							flex: "0 0 calc(50% - 0.5px)",
							overflow: "hidden",
						}}
					>
						{rowData.groupKeys ? (
							rowData.groupKeys.map((key) => (
								<Typography
									key={key}
									variant="body2"
									style={{
										whiteSpace: "normal",
										wordBreak: "break-word",
										overflowWrap: "break-word",
										hyphens: "auto",
										marginBottom: "4px",
									}}
								>
									{rowData.baseTranslations && rowData.baseTranslations[key]}{" "}
									<Typography
										variant="caption"
										color="textSecondary"
										component="span"
									>
										({resources[`${key}TranslationKey`]})
									</Typography>
								</Typography>
							))
						) : (
							<Typography
								variant="subtitle1"
								style={{
									whiteSpace: "normal",
									wordBreak: "break-word",
									overflowWrap: "break-word",
									hyphens: "auto",
								}}
							>
								{rowData.baseTranslations?.description}{" "}
								<Typography
									variant="caption"
									color="textSecondary"
									component="span"
								>
									({resources[`${draft.item?.key}TranslationKey`]})
								</Typography>
							</Typography>
						)}

						<InfoPanel
							showTransition={info.showTransition}
							severity={info.severity}
							message={info.message}
						/>
					</Box>

					<Divider orientation="vertical" flexItem style={{ margin: 0 }} />

					<Box
						display="flex"
						alignItems="center"
						justifyContent="space-between"
						height="100%"
						p={2}
						style={{ flex: "1" }}
					>
						<Box flex="1">
							{draft.item ? (
								<Box>
									<ThemeProvider theme={SelectTheme}>
										<Tooltip 
											title={draft.item.wasTrimmed ? `Warning: Translation was trimmed from ${draft.item.originalLength} to ${draft.item.maxLength} characters` : ""}
											placement="top"
											disableHoverListener={!draft.item.wasTrimmed}
										>
											<TextField
												id={`translation-${draft.item.key}-${rowData.id}-${rowData.type}`}
												fullWidth
												variant="outlined"
												size="small"
												value={draft.item.value}
												onChange={handleSingleChange}
												onBlur={(e) => handleBlur(draft.item.key, e.target.value)}
												label={resources["EnterTranslation"]}
												InputLabelProps={{ shrink: true }}
												inputProps={{ maxLength: draft.item.maxLength }}
												color={draft.item.wasTrimmed ? "secondary" : "primary"}
												error={draft.item.wasTrimmed || !!duplicateKeys[draft.item.key]}
												helperText={draft.item.wasTrimmed ? "Translation was trimmed due to length limit" : ""}
											/>
										</Tooltip>
									</ThemeProvider>
								</Box>
							) : draft.group ? (
								<Box
									display="flex"
									flexDirection="column"
									style={{ gap: "18px" }}
								>
									{draft.group.map((item) => (
										<Box key={item.key}>
											<ThemeProvider theme={SelectTheme}>
												<TextField
													id={`translation-${item.key}-${rowData.id}-${rowData.type}`}
													fullWidth
													variant="outlined"
													size="small"
													label={resources["EnterTranslation"]}
													value={item.value}
													onChange={(e) => handleGroupChange(item.key, e)}
													onBlur={(e) => handleBlur(item.key, e.target.value)}
													InputLabelProps={{ shrink: true }}
													inputProps={{ maxLength: item.maxLength }}
													color={item.wasTrimmed ? "secondary" : "primary"}
													error={item.wasTrimmed || !!duplicateKeys[item.key]}
													helperText={item.wasTrimmed ? resources["TranslationWasTrimmed"] : ""}
												/>
											</ThemeProvider>
										</Box>
									))}
								</Box>
							) : null}
						</Box>

						{isSynced && translationCompleted && (
							<IconButton
								id={`delete-translation-row-${rowData.id}`}
								onClick={() => setDeleteModalOpen(true)}
								style={{ marginLeft: "8px" }}
								color="primary"
							>
								<DeleteOutline />
							</IconButton>
						)}
					</Box>
				</Box>
				<DeleteConfirmationModal
					open={deleteModalOpen}
					onClose={() => {
						setDeleteModalOpen(false);
					}}
					onConfirm={handleSubmitDelete}
					resources={resources}
					areYouSureMessage={resources["AreYouSureYouWantToDeleteTranslation"]}
				/>
			</Paper>
		);
	},
	(prevProps, nextProps) =>
		Object.is(prevProps.rowData, nextProps.rowData) &&
		prevProps.selectedLang === nextProps.selectedLang
);
